import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown } from "lucide-react";
import { MaskReveal } from "./motion";
import { BUSINESS } from "@/lib/api";
import { trackConversion } from "@/lib/gtag";

const HERO_IMG =
  "https://customer-assets-v7afamib.emergentagent.net/job_grillpro-hyd/artifacts/6jo97m2p_IMG-20251124-WA0094.webp";

export default function Hero({ onQuote }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen pt-[92px] overflow-hidden bg-white">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-8 items-center min-h-[calc(100vh-92px)]">
        {/* Left */}
        <div className="lg:col-span-6 relative z-10 pt-10 lg:pt-0">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="font-label text-xs tracking-[0.3em] uppercase text-steel-600 mb-6 flex items-center gap-3"
          >
            <span className="h-px w-10 bg-steel-400" /> Hyderabad · Balcony & Window Safety
          </motion.p>

          <h1 aria-label="Safety you won't see. Views you won't lose." className="font-display font-black text-steel-900 tracking-tighter leading-[0.92] text-5xl md:text-7xl lg:text-[5.4rem]">
            <MaskReveal lines={["Safety you", "won't see."]} delay={0.15} />
            <span className="block text-gold-500">
              <MaskReveal lines={["Views you", "won't lose."]} delay={0.5} />
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }}
            className="mt-8 text-base md:text-lg text-steel-600 max-w-md leading-relaxed"
          >
            Premium invisible grills for high-rise balconies and windows — near-invisible
            SS304 & SS316 steel cables that keep children & pets safe without touching your skyline view.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <button
              onClick={onQuote}
              data-testid="hero-quote-btn"
              className="group bg-steel-800 text-white px-8 py-4 font-semibold flex items-center justify-center gap-3 hover:bg-steel-900 transition-colors"
            >
              Get a Free Quote
              <ArrowDown size={18} className="group-hover:translate-y-1 transition-transform" />
            </button>
            <a
              href={`tel:+91${BUSINESS.phone}`}
              onClick={trackConversion}
              data-testid="hero-call-btn"
              className="border border-steel-300 text-steel-900 px-8 py-4 font-semibold flex items-center justify-center gap-3 hover:bg-steel-50 transition-colors"
            >
              <Phone size={18} /> {BUSINESS.phoneDisplay}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
            className="mt-12 flex gap-8"
          >
            {[["SS316", "Marine Grade"], ["Warranty", "Written Cover"], ["Free Visit", "Measurement & Quote"]].map(([n, l]) => (
              <div key={l}>
                <div className="font-display font-bold text-2xl text-steel-900">{n}</div>
                <div className="font-label text-xs uppercase tracking-wider text-steel-500">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right image */}
        <div className="lg:col-span-6 relative h-[52vh] lg:h-[80vh]">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 overflow-hidden clip-slant border border-steel-200"
          >
            <motion.img
              src={HERO_IMG}
              alt="Invisible grills installed on a Hyderabad apartment balcony"
              width={1200}
              height={1600}
              fetchPriority="high"
              decoding="async"
              style={{ y, scale }}
              className="w-full h-[112%] object-cover"
            />
            <div className="absolute inset-0 grill-lines pointer-events-none" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2 }}
            className="absolute -bottom-5 left-4 lg:-left-8 bg-white border border-steel-200 shadow-[0_8px_32px_rgba(0,0,0,0.06)] px-5 py-4 max-w-[220px]"
          >
            <div className="font-label text-[10px] uppercase tracking-[0.2em] text-steel-500">Real project</div>
            <div className="font-display font-bold text-steel-900 leading-tight mt-1">Corner balcony, Hitech City</div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
